import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import {
  FiCalendar, FiTrendingUp, FiUsers, FiAward,
  FiDollarSign, FiRefreshCw, FiAlertCircle, FiCheck,
  FiClock, FiInfo, FiZap
} from "react-icons/fi";

/* ═══════════════════════════════════════════
   STYLES — NEUMORPHISM WITH VIBRANT COLORS
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .rh-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d0d5dc 100%);
    min-height: 100%;
  }
  .rh-root *, .rh-root *::before, .rh-root *::after { box-sizing: border-box; }
  .rh-root ::-webkit-scrollbar { width: 4px; height: 4px; }
  .rh-root ::-webkit-scrollbar-track { background: #e0e5ec; border-radius: 99px; }
  .rh-root ::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 99px; }

  .rh-page {
    max-width: 1200px; margin: 0 auto;
    padding: 24px 20px;
    display: flex; flex-direction: column; gap: 24px;
  }
  @media (max-width: 600px) { .rh-page { padding: 6px 5px; gap: 16px; } }

  /* ══════════════════════════════
     HERO BANNER - NEUMORPHISM
  ══════════════════════════════ */
  .rh-hero {
    background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 32px 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    position: relative;
    overflow: hidden;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
  .rh-hero::before {
    content: ''; position: absolute; top: -80px; right: -60px;
    width: 280px; height: 280px; border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .rh-hero::after {
    content: ''; position: absolute; bottom: -40px; left: 25%;
    width: 180px; height: 180px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .rh-hero-left { position: relative; z-index: 1; }
  .rh-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(251,191,36,.2);
    border: 1px solid rgba(251,191,36,.4);
    border-radius: 99px; padding: 5px 14px;
    font-size: 11px; font-weight: 700; color: #fcd34d;
    letter-spacing: .07em; text-transform: uppercase; margin-bottom: 12px;
    backdrop-filter: blur(10px);
  }
  .rh-badge-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #fbbf24;
    animation: rh-pulse 2s infinite;
  }
  @keyframes rh-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .rh-hero-title {
    font-size: clamp(20px, 4vw, 28px); font-weight: 900;
    color: #fff; line-height: 1.2; margin-bottom: 6px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }
  .rh-hero-sub { font-size: 13px; font-weight: 500; color: rgba(255, 255, 255, 0.8); }

  /* Hero right: current month chip */
  .rh-hero-chip {
    position: relative; z-index: 1;
    background: rgba(255,255,255,.1);
    border: 1px solid rgba(255,255,255,.15);
    border-radius: 24px;
    padding: 18px 24px;
    text-align: center;
    min-width: 150px;
    backdrop-filter: blur(10px);
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.15), inset 1px 1px 2px rgba(255, 255, 255, 0.1);
  }
  .rh-hero-chip-label {
    font-size: 10px; font-weight: 800; color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase; letter-spacing: .07em; margin-bottom: 6px;
  }
  .rh-hero-chip-val {
    font-size: clamp(18px,3vw,26px); font-weight: 900; color: #fbbf24;
  }

  /* ══════════════════════════════
     PREDICTED CARD - NEUMORPHISM
  ══════════════════════════════ */
  .rh-predicted-card {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border-radius: 9px;
    padding: 24px;
    position: relative;
    overflow: hidden;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.4);
    transition: all 0.2s ease;
  }
  .rh-predicted-card:active {
    transform: translateY(2px);
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.08), inset -8px -8px 16px rgba(255, 255, 255, 0.4);
  }
  .rh-predicted-decoration {
    position: absolute;
    top: -30px;
    right: -30px;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, rgba(251,191,36,0.2) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  .rh-predicted-content {
    position: relative;
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .rh-predicted-icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    border-radius: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 8px 8px 16px rgba(245, 158, 11, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .rh-predicted-icon svg {
    color: white;
  }
  .rh-predicted-main {
    flex: 1;
  }
  .rh-predicted-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }
  .rh-predicted-title {
    font-size: 13px;
    font-weight: 800;
    color: #92400e;
    text-transform: uppercase;
    letter-spacing: .07em;
  }
  .rh-predicted-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
  }
  .rh-predicted-badge.credited {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    box-shadow: 4px 4px 8px rgba(16, 185, 129, 0.3);
  }
  .rh-predicted-badge.not-eligible {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    box-shadow: 4px 4px 8px rgba(239, 68, 68, 0.3);
  }
  .rh-predicted-amount {
    font-size: 36px;
    font-weight: 900;
    color: #78350f;
    margin-bottom: 8px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.05);
  }
  .rh-predicted-message {
    font-size: 13px;
    color: #92400e;
    margin-bottom: 12px;
    line-height: 1.5;
  }
  .rh-predicted-footer {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    backdrop-filter: blur(4px);
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  /* ══════════════════════════════
     SUMMARY CARDS - NEUMORPHISM
  ══════════════════════════════ */
  .rh-summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }
  /* Keep two cards per row on all screen sizes */

  .rh-stat-card {
    border-radius: 9px;
    padding: 22px;
    transition: all 0.2s ease;
    position: relative;
  }
  .rh-stat-card:active {
    transform: translateY(2px);
  }
  
  /* Individual card colors */
  .rh-stat-card.green { 
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    box-shadow: 12px 12px 24px rgba(16, 185, 129, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .rh-stat-card.blue { 
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    box-shadow: 12px 12px 24px rgba(59, 130, 246, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .rh-stat-card.purple { 
    background: linear-gradient(135deg, #f5f3ff, #ede9fe);
    box-shadow: 12px 12px 24px rgba(139, 92, 246, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .rh-stat-card.amber { 
    background: linear-gradient(135deg, #fffbeb, #fef3c7);
    box-shadow: 12px 12px 24px rgba(245, 158, 11, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .rh-stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
  }
  .rh-stat-card.green .rh-stat-icon {
    background: #10b981;
    color: white;
    box-shadow: 6px 6px 12px rgba(16, 185, 129, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .rh-stat-card.blue .rh-stat-icon {
    background: #3b82f6;
    color: white;
    box-shadow: 6px 6px 12px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .rh-stat-card.purple .rh-stat-icon {
    background: #8b5cf6;
    color: white;
    box-shadow: 6px 6px 12px rgba(139, 92, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .rh-stat-card.amber .rh-stat-icon {
    background: #f59e0b;
    color: white;
    box-shadow: 6px 6px 12px rgba(245, 158, 11, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }

  .rh-stat-val {
    font-size: clamp(18px, 2.5vw, 26px);
    font-weight: 900;
    margin-bottom: 4px;
  }
  .rh-stat-card.green .rh-stat-val { color: #064e3b; }
  .rh-stat-card.blue .rh-stat-val { color: #1e3a8a; }
  .rh-stat-card.purple .rh-stat-val { color: #4c1d95; }
  .rh-stat-card.amber .rh-stat-val { color: #78350f; }
  
  .rh-stat-label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .07em;
  }
  .rh-stat-card.green .rh-stat-label { color: #065f46; }
  .rh-stat-card.blue .rh-stat-label { color: #1e40af; }
  .rh-stat-card.purple .rh-stat-label { color: #5b21b6; }
  .rh-stat-card.amber .rh-stat-label { color: #92400e; }

  /* ══════════════════════════════
     SECTION LABEL
  ══════════════════════════════ */
  .rh-section-label {
    font-size: 11px;
    font-weight: 800;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: .09em;
    margin-bottom: -6px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rh-section-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #3b82f6, transparent);
    border-radius: 99px;
  }

  /* ══════════════════════════════
     TABLE CARD - NEUMORPHISM
  ══════════════════════════════ */
  .rh-table-card {
    background: #e8edf5;
    border-radius: 28px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    overflow: hidden;
  }
  .rh-table-wrap { overflow-x: auto; }
  .rh-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 800px;
  }
  .rh-table thead tr {
    background: linear-gradient(135deg, #1e3a8a, #1e40af);
  }
  .rh-table thead th {
    padding: 16px 18px;
    text-align: left;
    font-size: 10px;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: .08em;
    white-space: nowrap;
  }
  .rh-table tbody tr {
    border-bottom: 1px solid rgba(59, 130, 246, 0.15);
    transition: all .15s;
  }
  .rh-table tbody tr:last-child { border-bottom: none; }
  .rh-table tbody tr:hover {
    background: rgba(59, 130, 246, 0.05);
    transform: scale(1.01);
  }
  .rh-table td { padding: 16px 18px; vertical-align: middle; }

  /* Month cell */
  .rh-month-name {
    font-size: 14px;
    font-weight: 800;
    color: #1f2937;
  }

  /* Tier pill */
  .rh-tier-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 14px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 800;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .rh-tier-pill.gold {
    background: linear-gradient(135deg, #fef9c3, #fde68a);
    color: #92400e;
  }
  .rh-tier-pill.silver {
    background: linear-gradient(135deg, #f5f3ff, #ede9fe);
    color: #6d28d9;
  }
  .rh-tier-pill.bronze {
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    color: #1d4ed8;
  }
  .rh-tier-pill.none {
    background: #dce3ec;
    color: #6b7280;
  }

  /* Amount */
  .rh-amount {
    font-size: 15px;
    font-weight: 900;
    color: #059669;
  }

  /* Team investment */
  .rh-invest-main {
    font-size: 13.5px;
    font-weight: 800;
    color: #1f2937;
  }
  .rh-invest-pct {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    margin-top: 3px;
  }

  /* Active members */
  .rh-members-direct {
    font-size: 14px;
    font-weight: 800;
    color: #1f2937;
  }
  .rh-members-total {
    font-size: 11px;
    font-weight: 600;
    margin-top: 3px;
  }
  .rh-members-total.text-green-600 { color: #059669; }
  .text-green-600 { color: #059669; }
  .text-red-600 { color: #dc2626; }

  /* Status pill */
  .rh-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 800;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .rh-status-pill.credited {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #15803d;
  }
  .rh-status-pill.pending {
    background: linear-gradient(135deg, #fef9c3, #fde68a);
    color: #a16207;
  }

  /* ══════════════════════════════
     MOBILE CARD VIEW
  ══════════════════════════════ */
  .rh-cards-list {
    display: none;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }
  @media (max-width: 799px) {
    .rh-table-wrap { display: none; }
    .rh-cards-list { display: flex; }
  }

  .rh-record-card {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 20px;
    padding: 18px;
    transition: all 0.2s ease;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.5);
  }
  .rh-record-card:active {
    transform: translateY(2px);
    box-shadow: inset 6px 6px 12px rgba(0, 0, 0, 0.08), inset -6px -6px 12px rgba(255, 255, 255, 0.5);
  }
  .rh-rc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    gap: 10px;
  }
  .rh-rc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }
  .rh-rc-item-label {
    font-size: 9px;
    font-weight: 800;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: .07em;
    margin-bottom: 4px;
  }
  .rh-rc-item-val {
    font-size: 14px;
    font-weight: 900;
    color: #1f2937;
  }
  .rh-rc-item-sub {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    margin-top: 2px;
  }

  /* ══════════════════════════════
     EMPTY STATE
  ══════════════════════════════ */
  .rh-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    background: #e8edf5;
    border-radius: 20px;
    margin: 20px;
  }
  .rh-empty-icon {
    font-size: 52px;
    margin-bottom: 16px;
  }
  .rh-empty-title {
    font-size: 16px;
    font-weight: 800;
    color: #1f2937;
    margin-bottom: 8px;
  }
  .rh-empty-sub {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    max-width: 340px;
  }

  /* ══════════════════════════════
     TIPS BOX - NEUMORPHISM
  ══════════════════════════════ */
  .rh-tips {
    background: #e8edf5;
    border-radius: 28px;
    padding: 24px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .rh-tips-title {
    font-size: 13px;
    font-weight: 800;
    color: #1f2937;
    text-transform: uppercase;
    letter-spacing: .07em;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .rh-tips-title-bar {
    width: 4px;
    height: 20px;
    border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    flex-shrink: 0;
  }
  .rh-tips-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .rh-tip-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 20px;
    background: linear-gradient(135deg, #fffbeb, #fef3c7);
    font-size: 12.5px;
    font-weight: 600;
    color: #78350f;
    line-height: 1.6;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .rh-tip-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex-shrink: 0;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    margin-top: 2px;
    box-shadow: 3px 3px 6px rgba(245, 158, 11, 0.3);
  }

  /* ══════════════════════════════
     ERROR STATE - NEUMORPHISM
  ══════════════════════════════ */
  .rh-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    background: #e8edf5;
    border-radius: 28px;
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.08), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .rh-error-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }
  .rh-error-msg {
    font-size: 14px;
    font-weight: 700;
    color: #dc2626;
    margin-bottom: 20px;
  }
  .rh-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 30px;
    border: none;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 800;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    box-shadow: 8px 8px 16px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
    transition: all 0.2s;
  }
  .rh-retry-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }

  /* ══════════════════════════════
     LOADING SKELETONS
  ══════════════════════════════ */
  .rh-skeleton {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
  }
  .rh-skel {
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .rh-skel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(59, 130, 246, 0.1) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: rh-shimmer 1.4s infinite;
  }
  @keyframes rh-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .rh-skel-hero { height: 140px; }
  .rh-skel-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  @media (max-width: 900px) {
    .rh-skel-row { grid-template-columns: repeat(2, 1fr); }
  }
  .rh-skel-box {
    height: 120px;
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
  }
  .rh-skel-tbl { height: 300px; }
`;

const fmtCur = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const getTierInfo = (pct) => {
  switch (pct) {
    case 5: return { name: "Gold", cls: "gold" };
    case 3: return { name: "Silver", cls: "silver" };
    case 2: return { name: "Bronze", cls: "bronze" };
    default: return { name: "None", cls: "none" };
  }
};

const fmtMonth = (year, month) =>
  new Date(year, month - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const RoyaltyHistory = () => {
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [predicted, setPredicted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [spinning, setSpinning] = useState(false);

  const fetchRoyaltyHistory = async (fromBtn = false) => {
    try {
      if (fromBtn) setSpinning(true);
      setLoading(true);
      const token = localStorage.getItem("token");
      const { data } = await axios.get("/share/royalty-history", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(data);
      const totalEarned = data.reduce((s, r) => s + r.amount, 0);
      const monthsEarned = data.filter(r => r.amount > 0).length;
      const averageMonthly = monthsEarned > 0 ? totalEarned / monthsEarned : 0;
      setSummary({ totalEarned, monthsEarned, averageMonthly, currentMonth: data[0]?.amount || 0 });
      setError("");
    } catch (err) {
      setError("Failed to fetch royalty history");
      console.error(err);
    } finally {
      setLoading(false);
      if (fromBtn) setTimeout(() => setSpinning(false), 700);
    }
  };

  const fetchPredictedRoyalty = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get("/share/predicted-royalty", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPredicted(data);
    } catch (err) {
      console.error("Failed to fetch predicted royalty:", err);
    }
  };

  useEffect(() => {
    fetchRoyaltyHistory();
    fetchPredictedRoyalty();
  }, []);

  /* Loading */
  if (loading) return (
    <div className="rh-root">
      <style>{styles}</style>
      <div className="rh-skeleton">
        <div className="rh-skel rh-skel-hero" />
        <div className="rh-skel-row">{[1, 2, 3, 4].map(i => <div className="rh-skel-box" key={i} />)}</div>
        <div className="rh-skel rh-skel-tbl" />
      </div>
    </div>
  );

  /* Error */
  if (error) return (
    <div className="rh-root">
      <style>{styles}</style>
      <div className="rh-page">
        <div className="rh-error">
          <div className="rh-error-icon">⚠️</div>
          <div className="rh-error-msg">{error}</div>
          <button className="rh-retry-btn" onClick={() => fetchRoyaltyHistory()}>
            <FiRefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="rh-root">
      <style>{styles}</style>
      <div className="rh-page">

        {/* Hero */}
        <div className="rh-hero">
          <div className="rh-hero-left">
            <div className="rh-hero-badge">
              <div className="rh-badge-dot" />
              Royalty Program
            </div>
            <div className="rh-hero-title">Growth Incentive Partner Income History</div>
            <div className="rh-hero-sub">Track your monthly royalty earnings and tier progression</div>
          </div>
          {summary && (
            <div className="rh-hero-chip">
              <div className="rh-hero-chip-label">Current Month</div>
              <div className="rh-hero-chip-val">{fmtCur(summary.currentMonth)}</div>
            </div>
          )}
        </div>

        {/* Predicted Royalty Card */}
        {predicted && (
          <div className="rh-predicted-card">
            <div className="rh-predicted-decoration" />
            <div className="rh-predicted-content">
              <div className="rh-predicted-icon">
                <FiCalendar size={24} />
              </div>
              <div className="rh-predicted-main">
                <div className="rh-predicted-header">
                  <h4 className="rh-predicted-title">Previous Month's Royalty</h4>
                  <div className="rh-predicted-header-right">
                    <div className="rh-predicted-badge ${predicted.status === 'credited' ? 'credited' : 'not-eligible'}">
                      {predicted.status === 'credited' ? (
                        <>
                          <FiCheck size={12} />
                          <span>Credited</span>
                        </>
                      ) : (
                        <>
                          <span>⛔</span>
                          <span>Not Eligible</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="rh-predicted-amount">
                  {fmtCur(predicted.predictedRoyalty)}
                </div>
                <div className="rh-predicted-message">
                  {predicted.message}
                </div>
                {predicted.status === 'credited' && (
                  <div className="rh-predicted-footer">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    <FiCheck size={12} />
                    <span>Processed on {predicted.processingDate}</span>
                  </div>
                )}
                {predicted.status === 'not_eligible' && (
                  <div className="rh-predicted-footer">
                    <span>ℹ️</span>
                    <span>Check eligibility criteria for next month</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="rh-summary">
            <StatCard variant="green" icon={<FiDollarSign size={20} />} label="Total Earned" val={fmtCur(summary.totalEarned)} />
            <StatCard variant="blue" icon={<FiCalendar size={20} />} label="Months Earned" val={summary.monthsEarned} />
            <StatCard variant="purple" icon={<FiTrendingUp size={20} />} label="Average Monthly" val={fmtCur(Math.round(summary.averageMonthly))} />
            <StatCard variant="amber" icon={<FiAward size={20} />} label="This Month" val={fmtCur(summary.currentMonth)} />
          </div>
        )}

        {/* Table Section */}
        <div className="rh-section-label">Monthly Records</div>
        <div className="rh-table-card">

          {history.length === 0 ? (
            <div className="rh-empty">
              <div className="rh-empty-icon">🏆</div>
              <div className="rh-empty-title">No royalty history yet</div>
              <div className="rh-empty-sub">
                Royalty earnings will appear here once you become eligible. Build your team to unlock royalty income!
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="rh-table-wrap">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Tier</th>
                      <th>Amount</th>
                      <th>Team Loan Pay</th>
                      <th>My Loan Pay</th>
                      <th>Active Directs</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(rec => {
                      const tier = getTierInfo(rec.royaltyPercentage);
                      return (
                        <tr key={`${rec.year}-${rec.month}`}>
                          <td>
                            <div className="rh-month-name">{fmtMonth(rec.year, rec.month)}</div>
                          </td>
                          <td>
                            <span className={`rh-tier-pill ${tier.cls}`}>
                              <FiAward size={11} /> {tier.name} {rec.royaltyPercentage > 0 && `(${rec.royaltyPercentage}%)`}
                            </span>
                          </td>
                          <td>
                            <div className="rh-amount">{fmtCur(rec.amount)}</div>
                          </td>
                          <td>
                            <div className="rh-invest-main">{fmtCur(rec.downlineInvestment)}</div>
                            {/* <div className="rh-invest-pct">{rec.personalInvestmentPercentage.toFixed(1)}% personal ratio</div> */}
                          </td>
                          <td>
                            <div className="rh-invest-main">{fmtCur(rec.personalInvestment)}</div>
                          </td>
                          <td>
                            <div className="rh-members-direct">{rec.activeDirectMembers} direct</div>
                            <div className={`rh-members-total ${rec.totalActiveMembers >= 30 ? "text-green-600" : "text-red-600"}`}>
                              {rec.totalActiveMembers} / 30 active
                            </div>
                          </td>
                          <td>
                            {rec.status === "credited"
                              ? <span className="rh-status-pill credited"><FiCheck size={11} /> Credited</span>
                              : <span className="rh-status-pill pending"><FiClock size={11} /> Pending</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="rh-cards-list">
                {history.map(rec => {
                  const tier = getTierInfo(rec.royaltyPercentage);
                  return (
                    <div key={`${rec.year}-${rec.month}`} className="rh-record-card">
                      <div className="rh-rc-header">
                        <div className="rh-month-name">{fmtMonth(rec.year, rec.month)}</div>
                        <span className={`rh-tier-pill ${tier.cls}`}>
                          <FiAward size={10} /> {tier.name}
                        </span>
                      </div>
                      <div className="rh-rc-grid">
                        <div>
                          <div className="rh-rc-item-label">Amount</div>
                          <div className="rh-rc-item-val" style={{ color: "#059669" }}>{fmtCur(rec.amount)}</div>
                          <div className="rh-rc-item-sub">Royalty</div>
                        </div>
                        <div>
                          <div className="rh-rc-item-label">Team Loan Pay</div>
                          <div className="rh-rc-item-val">{fmtCur(rec.downlineInvestment)}</div>
                          {/* <div className="rh-rc-item-sub">{rec.personalInvestmentPercentage.toFixed(1)}% personal</div> */}
                        </div>
                        <div>
                          <div className="rh-rc-item-label">My Loan Pay</div>
                          <div className="rh-rc-item-val">{fmtCur(rec.personalInvestment)}</div>
                        </div>
                        <div>
                          <div className="rh-rc-item-label">Active Directs</div>
                          <div className="rh-rc-item-val">{rec.activeDirectMembers}</div>
                          <div className={`rh-rc-item-sub ${rec.totalActiveMembers >= 30 ? "text-green-600" : "text-red-600"}`}>
                            {rec.totalActiveMembers} / 30 active
                          </div>
                        </div>
                        <div>
                          <div className="rh-rc-item-label">Status</div>
                          <div style={{ marginTop: 3 }}>
                            {rec.status === "credited"
                              ? <span className="rh-status-pill credited"><FiCheck size={10} /> Credited</span>
                              : <span className="rh-status-pill pending"><FiClock size={10} /> Pending</span>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Tips Section */}
        <div className="rh-section-label">How to Grow</div>
        <div className="rh-tips">
          <div className="rh-tips-title">
            <div className="rh-tips-title-bar" /> Royalty Program Tips
          </div>
          <div className="rh-tips-list">
            {[
              "Maintain 30+ active downline members and ₹1,00,000+ monthly team investment for eligibility",
              "Increase your personal investment to unlock higher tiers — Bronze (₹5,000+,2%), Silver (₹7,000+,3%), Gold (₹10,000+,5%)",
              "Royalty is automatically processed on the 5th of every month at 2 AM",
            ].map((tip, i) => (
              <div className="rh-tip-item" key={i}>
                <div className="rh-tip-icon"><FiZap size={12} /></div>
                {tip}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

/* StatCard Component */
const StatCard = ({ variant, icon, label, val }) => (
  <div className={`rh-stat-card ${variant}`}>
    <div className="rh-stat-icon">{icon}</div>
    <div className="rh-stat-val">{val}</div>
    <div className="rh-stat-label">{label}</div>
  </div>
);

export default RoyaltyHistory;